import { createContext, useContext, useMemo, type ReactNode } from 'react';

import { type ThemeProviderLogic, type ThemeProviderProps } from './ThemeProvider.types';
import { useThemeProviderLogic } from './useThemeProviderLogic';

const ThemeContext = createContext<ThemeProviderLogic | null>(null);

/**
 * Applies the theme to `<html>` and exposes it to the shell's toggle.
 *
 * Presentation-only, per U5/W2: every decision lives in `useThemeProviderLogic`.
 * There is no styling here at all — the theme is a class on the document root
 * that the Sanctuary preset keys off, so components stay written against tokens
 * and never ask which theme is active (U6).
 */
export function ThemeProvider({ children }: ThemeProviderProps): ReactNode {
  const logic = useThemeProviderLogic();
  const value = useMemo(() => logic, [logic]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeProviderLogic {
  const context = useContext(ThemeContext);

  if (context === null) {
    throw new Error('useTheme must be used inside <ThemeProvider>.');
  }

  return context;
}
